import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useHistory, useLocation, useParams } from 'react-router';
import Helmet from 'react-helmet';
import cx from 'classnames';
import { Button } from '@tovia/man-ui/lib/components/Button/Button';
import { Modal } from '@tovia/man-ui/lib/components/Modal/Modal';
import RatingSlider from '@tovia/man-ui/lib/components/RatingSlider/RatingSlider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faTh,
  faPlay,
  faHeart,
  faStar,
  faPause,
  faCaretDown,
  faCaretUp,
} from '@fortawesome/pro-solid-svg-icons';
import { faArrowToBottom } from '@fortawesome/pro-regular-svg-icons';
import Toggle from 'react-toggle';
import { ImageViewerPlaceholder } from 'src/client/components/Placeholders/Layouts/ImageViewerPlaceholder';
import { load, reset } from 'src/client/redux/modules/image';
import { addFavor as addFavorFn, removeFavor as removeFavorFn } from 'src/client/redux/modules/favoriteInfo';
import { rateItem as rateItemFn } from 'src/client/redux/modules/ratingInfo';
import { setValue } from 'src/client/redux/modules/settings';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';
import useHammerEvents from './useHammerEvents';
import { redirectToJoin, useJoinUrl, useJoinUrlGenerator } from 'src/client/components/buttons/JoinButton';
import useAccessToken from '../../helpers/useAccessToken';
import { ImageRequest } from 'src/@types/app';

const IDLETIME = 4000; // user is idle after 4s of inactivity

const SlideshowProgress = styled.div<{ slideshowSpeed: number }>`
  animation-duration: ${(props) => props.slideshowSpeed / 1000}s;
  animation-name: progressUp;
  animation-timing-function: linear;
  background: rgba(74, 144, 226, 0.5);
  height: 3px;
  position: absolute;
  top: 0;
  z-index: 2;

  &.paused {
    animation-play-state: paused;
  }
`;

type LocationState = {
  galleryPage: number;
  prevPath: string;
};

type QueryParams = {
  quality: string;
  imageNumber: string;
  galleryName: string;
  date: string;
  mediaType: string;
};

export default function Image() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const params = useParams<QueryParams>();
  const generateJoinUrl = useJoinUrlGenerator();

  const { accessToken, accessTokenQueryParam } = useAccessToken();

  const site = useSelector((state) => state.site);
  const { user, loaded: userLoaded } = useSelector((state) => state.auth);
  const item = useSelector((state) => state.image.item);
  const error = useSelector((state) => state.image.error);
  const loaded = useSelector((state) => state.image.loaded);
  const favorites = useSelector((state) => state.favoriteInfo.favoritingObjectUUIDs);
  const ratings = useSelector((state) => state.ratingInfo.ratings);

  const resolution = useSettingsSelector('resolution');
  const imageAutoSize = useSettingsSelector('imageAutoSize');
  const template = useSettingsSelector('template');
  const slideshowDelay = useSettingsSelector('slideshowDelay') as number;

  const loadImage = useCallback((params: ImageRequest) => dispatch(load(params)), [dispatch]);
  const resetImage = useCallback(() => dispatch(reset()), [dispatch]);
  const addFavor = useCallback((args) => dispatch(addFavorFn(args)), [dispatch]);
  const removeFavor = useCallback((args) => dispatch(removeFavorFn(args)), [dispatch]);
  const rateItem = useCallback((args) => dispatch(rateItemFn(args)), [dispatch]);
  const setSettingsValue = useCallback((...args: [string, string | number]) => dispatch(setValue(...args)), [dispatch]);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFavorited, setImgFavorited] = useState(false);
  const [imgRated, setImgRated] = useState(false);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowPlaying, setSlideshowPlaying] = useState(false);
  const [slideshowProgressBarVisible, setSlideshowProgressBarVisible] = useState(false);
  const [slideTimerRemaining, setSlideTimerRemaining] = useState(0);
  const [slideTimerStartTime, setSlideTimerStartTime] = useState(0);
  const [ratingsActive, setRatingsActive] = useState(false);
  const [imgNavigationDisabled, setImgNavigationDisabled] = useState(false);
  const [imgResolution, setImgResolution] = useState(params.quality || resolution || 'low');
  const [imageNumber, setImageNumber] = useState(parseInt(params.imageNumber.split('#')[0])); // We are removing any hashParams which might be included in imageNumber
  const [userIsIdle, setUserIsIdle] = useState(false);
  const [footerCollapsed, setFooterCollapsed] = useState(false);
  const [prevImgLoaded, setPrevImgLoaded] = useState(false);
  const [nextImgLoaded, setNextImgLoaded] = useState(false);
  const [loadedImgNumber, setLoadedImgNumber] = useState(0);

  const isUser = userLoaded && !!user;

  const slideshowSpeed = useMemo(() => (slideshowDelay ? slideshowDelay * 1000 : 3000), [slideshowDelay]);
  const isMobile = window.innerWidth <= 767;
  const autoSize = useMemo(() => imageAutoSize === 'on' || isMobile, [imageAutoSize, isMobile]);

  const leftNavigationButton = useRef(null);
  const rightNavigationButton = useRef(null);

  const slideshowTimer = useRef(0);
  const idleTimer = useRef(0);

  const element = useRef<HTMLImageElement | null>(null);
  const [joinUrl] = useJoinUrl({ campaign: 'gallery-image-error' });

  useEffect(() => {
    if (error?.status === 401) {
      window.location.replace(joinUrl);
    } else if ((item?.media && item.media.length < imageNumber) || error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history, imageNumber, item, joinUrl]);

  const redirectToJoinIfGuest = useCallback(() => {
    if (!isUser) {
      redirectToJoin(generateJoinUrl, 'gallery-image-action');
    }

    return !isUser;
  }, [generateJoinUrl, isUser]);

  const toggleRatings = useCallback(() => {
    if (redirectToJoinIfGuest()) {
      return;
    }
    const toggle = !ratingsActive;
    setRatingsActive(toggle);
    setImgNavigationDisabled(toggle);
  }, [ratingsActive, redirectToJoinIfGuest]);

  const handleIsImgFavorited = useCallback(() => {
    if (!item) return;
    const { media } = item;
    const index = imageNumber - 1;

    const favorited = favorites.includes(media[index].UUID);
    setImgFavorited(favorited);
  }, [favorites, imageNumber, item]);

  const handleIsImgRated = useCallback(() => {
    if (!item) return;
    const { media } = item;
    const index = imageNumber - 1;

    const mediaUUID = media[index].UUID;

    if (ratings) {
      const rated = !!ratings[mediaUUID];
      setImgRated(rated);
    }
  }, [imageNumber, item, ratings]);

  const generateImgUrl = useMemo(() => {
    if (item && item.media.length >= imageNumber) {
      const { path } = item;
      return [path, 'image', imageNumber].join('/');
    }

    return '';
  }, [imageNumber, item]);

  const updateUrl = useCallback(() => {
    if (!generateImgUrl) {
      return;
    }

    const galleryPageHash = location.hash || `#galleryPage=${location.state?.galleryPage || 1}`;

    history.replace(`${generateImgUrl}/${imgResolution}/${accessTokenQueryParam}${galleryPageHash}`, location.state);
  }, [accessTokenQueryParam, generateImgUrl, history, imgResolution, location.hash, location.state]);

  useEffect(() => {
    if (item) {
      if (!element.current) {
        element.current = document.querySelector('[data-image=current]');
      }

      updateUrl();
    }
  }, [item, updateUrl]);

  useEffect(() => {
    if (!imgLoaded && element.current) {
      element.current.style.visibility = 'hidden';
    }
  }, [imgLoaded]);

  const handleImageNavigationButtonClick = useCallback(
    (direction) => {
      if (!item) return;

      const onFirstSlide = imageNumber === 1;
      const onLastSlide = item.media.length === imageNumber;

      if ((direction === 'left' && onFirstSlide) || (direction === 'right' && onLastSlide)) {
        return;
      }

      // Hide ratings popover after a keydown input for next/prev image which will re-enable imgNavigation
      if (imgNavigationDisabled) {
        if (ratingsActive) {
          toggleRatings();
        }
        return;
      }

      if ((direction === 'left' && !prevImgLoaded) || (direction === 'right' && !nextImgLoaded)) {
        setImgLoaded(false);
      }
      setPrevImgLoaded(false);
      setNextImgLoaded(false);

      setSlideshowProgressBarVisible(false);

      const currentImageNumber = imageNumber;
      const finalNumber = direction === 'left' ? currentImageNumber - 1 : currentImageNumber + 1;

      setImageNumber(finalNumber);

      window.mixpanel.track('Gallery Image navigate', {
        galleryUUID: item.galleryUUID,
        direction,
        nextImageNumber: finalNumber,
        currentImageNumber,
      });
    },
    [imageNumber, imgNavigationDisabled, item, nextImgLoaded, prevImgLoaded, ratingsActive, toggleRatings],
  );

  const endSlideshow = useCallback(() => {
    if (slideshowTimer) {
      window.clearTimeout(slideshowTimer.current);

      setSlideshowActive(false);
      setSlideshowPlaying(false);
      setSlideshowProgressBarVisible(false);
    }
  }, []);

  const slideshowTransition = useCallback(() => {
    if (!item) return;

    const { media } = item;
    const onLastSlide = media.length === imageNumber;

    let nextSlideImageNumber = imageNumber + 1;

    setSlideshowProgressBarVisible(false);

    if (imgNavigationDisabled) {
      endSlideshow();
      return;
    }

    // has to be this way because 'nextImgLoaded' can become stale. this method is being used in a setTimeout
    // which means that it won't use updated state values (assuming they updated). doing this ensures we have
    // the most up to date state to check.
    setNextImgLoaded((prevState) => {
      if (!prevState) {
        setImgLoaded(false);
      }
      return false;
    });
    setPrevImgLoaded(false);

    if (onLastSlide) {
      nextSlideImageNumber = 1;
    }

    setImageNumber(nextSlideImageNumber);
    setSlideTimerStartTime(Date.now());
  }, [endSlideshow, imageNumber, imgNavigationDisabled, item]);

  const nextSlideTimer = useCallback(() => {
    window.clearTimeout(slideshowTimer.current);

    slideshowTimer.current = window.setTimeout(slideshowTransition, slideshowSpeed);
    setSlideTimerStartTime(Date.now());
  }, [slideshowSpeed, slideshowTransition]);

  const startSlideshow = useCallback(() => {
    setSlideshowActive(true);
    setSlideshowPlaying(true);
    setSlideshowProgressBarVisible(true);

    nextSlideTimer();
  }, [nextSlideTimer]);

  const toggleSlideshow = useCallback(() => {
    if (!slideshowActive) {
      startSlideshow();
    } else {
      endSlideshow();
    }
  }, [endSlideshow, slideshowActive, startSlideshow]);

  const startIdleTimer = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    setUserIsIdle(false);
    idleTimer.current = window.setTimeout(() => setUserIsIdle(true), IDLETIME);
  }, []);

  // need to make this ref so that useHammerEvents gets the most updated callback function for swipeEvent
  // if you just send the actual function (handleImageNavigationButtonClick) it will not work properly
  // might be a bug somewhere? or passing useCallback functions in custom hooks might not be supported
  // if the dependencies are updated later?
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const swipeEvent = useRef<any>(null);
  useEffect(() => {
    swipeEvent.current = handleImageNavigationButtonClick;
  }, [handleImageNavigationButtonClick]);

  useHammerEvents(autoSize, loadedImgNumber, startIdleTimer, swipeEvent);

  const handleIsUserIdle = useCallback(
    (event) => {
      if (event?.target?.tagName?.toLowerCase() === 'img') {
        // lift responsibility to handleNavigationAfterImageClick fn
        return;
      }

      startIdleTimer();
    },
    [startIdleTimer],
  );

  const handleKeyDown = useCallback(
    (e) => {
      const leftArrowKeyPressed = e.code === 'ArrowLeft';
      const rightArrowKeyPressed = e.code === 'ArrowRight';
      const upArrowKeyPressed = e.code === 'ArrowUp';
      const downArrowKeyPressed = e.code === 'ArrowDown';
      const spacebarPressed = e.code === 'Space';
      const escapeKeyPressed = e.code === 'Escape';

      if (leftArrowKeyPressed) {
        if (leftNavigationButton.current) {
          handleImageNavigationButtonClick('left');
        }

        return;
      }

      if (rightArrowKeyPressed) {
        if (rightNavigationButton.current) {
          handleImageNavigationButtonClick('right');
        }

        return;
      }

      if (spacebarPressed) {
        e.preventDefault();
        e.stopPropagation();
        toggleSlideshow();
        startIdleTimer();
      }

      if (escapeKeyPressed) {
        history.goBack();
      }

      if (upArrowKeyPressed) {
        setFooterCollapsed(false);
        startIdleTimer();
      }

      if (downArrowKeyPressed) {
        setFooterCollapsed(true);
        setUserIsIdle(true);
      }
    },
    [handleImageNavigationButtonClick, history, startIdleTimer, toggleSlideshow],
  );

  const pauseSlideshow = useCallback(() => {
    if (!slideshowActive) {
      return;
    }

    const remainingTime = slideshowSpeed - (Date.now() - slideTimerStartTime);

    window.clearTimeout(slideshowTimer.current);

    setSlideshowPlaying(false);
    setSlideTimerRemaining(remainingTime);
  }, [slideTimerStartTime, slideshowActive, slideshowSpeed]);

  const resumeSlideshow = useCallback(() => {
    if (slideshowPlaying || !slideshowActive) {
      return;
    }

    setSlideshowPlaying(true);

    window.clearTimeout(slideshowTimer.current);
    slideshowTimer.current = window.setTimeout(slideshowTransition, slideTimerRemaining);
  }, [slideTimerRemaining, slideshowActive, slideshowPlaying, slideshowTransition]);

  const loadData = useCallback(() => {
    const loadParams = {
      name: params.galleryName,
      date: params.date,
      order: params.imageNumber,
      mediaType: params.mediaType as ImageRequest['mediaType'],
      token: accessToken,
    };

    loadImage(loadParams);
  }, [accessToken, loadImage, params.date, params.galleryName, params.imageNumber, params.mediaType]);

  useEffect(() => {
    if (!item && !loaded) {
      document.documentElement.classList.add('showing-imageViewer');
      loadData();
      handleIsUserIdle(false);
    }
  }, [handleIsUserIdle, item, loadData, loaded]);

  useEffect(
    () => () => {
      document.documentElement.classList.remove('showing-imageViewer');
      resetImage();
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(slideshowTimer.current);
    },
    [resetImage],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleIsUserIdle);
    window.addEventListener('click', handleIsUserIdle);
    window.addEventListener('mousedown', pauseSlideshow);
    window.addEventListener('mouseup', resumeSlideshow);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleIsUserIdle);
      window.removeEventListener('click', handleIsUserIdle);
      window.removeEventListener('mousedown', pauseSlideshow);
      window.removeEventListener('mouseup', resumeSlideshow);
    };
  }, [handleIsUserIdle, handleKeyDown, pauseSlideshow, resumeSlideshow]);

  const eventTrigger = ({
    galleryUUID,
    galleryName,
    galleryPath,
    imageNumber: number,
    quality,
    models,
    photographers,
  }) => {
    window.mixpanel.track('Gallery Image view', {
      galleryUUID,
      galleryName,
      galleryPath,
      imageNumber: number,
      quality,
      models,
      photographers,
    });
    window.dataLayer.push({
      event: 'select_content',
      content_type: 'image',
      item_id: `${galleryName}_${number}`,
    });
  };

  useEffect(() => {
    if (item) {
      eventTrigger({
        galleryUUID: item.galleryUUID,
        galleryName: item.galleryName,
        galleryPath: item.path,
        models: item.models.map((model) => model.name),
        photographers: item.photographers.map((photographer) => photographer.name),
        imageNumber,
        quality: imgResolution,
      });
    }
  }, [imageNumber, imgResolution, item]);

  useEffect(() => {
    if (item && favorites?.length > 0) {
      handleIsImgFavorited();
    }

    if (item && ratings && Object.keys(ratings).length > 0) {
      handleIsImgRated();
    }
  }, [favorites, handleIsImgFavorited, handleIsImgRated, item, ratings]);

  const generateMetaTitle = useMemo(() => {
    if (!item?.models) {
      return undefined;
    }

    const { models, galleryName } = item;

    return `${models.map((model) => model.name).join(' & ')} - "${galleryName}" :: ${
      site.name
    } - Image #${imageNumber}`;
  }, [imageNumber, item, site.name]);

  const handleImgLoad = useCallback(
    (e) => {
      setImgLoaded(true);
      setLoadedImgNumber(e.target.dataset.index);

      if (slideshowActive) {
        nextSlideTimer(); // If slideshow is playing load the slide
        setSlideshowProgressBarVisible(true);
      }
    },
    [nextSlideTimer, slideshowActive],
  );

  const handlePrevImgLoad = useCallback(() => {
    setPrevImgLoaded(true);
  }, []);

  const handleNextImgLoad = useCallback(() => {
    setNextImgLoaded(true);
  }, []);

  const handleResolutionChange = useCallback(
    (event) => {
      if (redirectToJoinIfGuest()) {
        return;
      }
      const targetResolution = event.target.className.match(/high|medium|low/g);

      if (!targetResolution) {
        return;
      }

      const [selectedResolution] = targetResolution;

      if (imgResolution === selectedResolution) {
        return;
      }

      setLoadedImgNumber(0);
      setPrevImgLoaded(false);
      setNextImgLoaded(false);
      setImgLoaded(false);
      setImgResolution(selectedResolution);
      setSettingsValue('resolution', selectedResolution);
    },
    [imgResolution, redirectToJoinIfGuest, setSettingsValue],
  );

  const toggleAutoSize = useCallback(
    (event) => {
      if (redirectToJoinIfGuest()) {
        return;
      }
      setSettingsValue('imageAutoSize', event.target.checked ? 'on' : 'off');
    },
    [redirectToJoinIfGuest, setSettingsValue],
  );

  const toggleImgFavorited = useCallback(() => {
    if (!item || redirectToJoinIfGuest()) {
      return;
    }
    const { media } = item;

    setImgFavorited((prevState) => !prevState);

    const index = imageNumber - 1;

    const actionParams = {
      objectUUID: media[index].UUID,
      objectType: 'MEDIA',
    };

    if (imgFavorited) {
      removeFavor(actionParams);
    } else {
      addFavor(actionParams);
    }
  }, [addFavor, imageNumber, imgFavorited, item, redirectToJoinIfGuest, removeFavor]);

  const toggleExpandCollapse = useCallback(() => {
    const change = !footerCollapsed;
    setFooterCollapsed(change);
    setUserIsIdle(change);
  }, [footerCollapsed]);

  const onRate = useCallback(
    (rating) => {
      if (!item) return;
      const { media } = item;

      const index = imageNumber - 1;
      const mediaUUID = media[index].UUID;

      handleIsImgRated();

      rateItem({
        objectUUID: mediaUUID,
        objectType: 'MEDIA',
        rating,
      });
    },
    [handleIsImgRated, imageNumber, item, rateItem],
  );

  const renderBackButton = useCallback(() => {
    let galleryUrl;

    if (item?.path) {
      const { path } = item;
      const galleryPage = location.hash ? location.hash.split('=')[1] : location.state?.galleryPage || 1;
      galleryUrl = `${path.includes('gallery') ? `${path}/${galleryPage}${accessTokenQueryParam}` : path}`;
    }

    return (
      <Button
        className="button back"
        {...(location.state?.prevPath
          ? {
              onClick: () => {
                window.history.replaceState({ restoreScroll: true }, '', location.state.prevPath);
                history.goBack();
              },
            }
          : { url: galleryUrl })}
        disabled={!item}
      >
        <FontAwesomeIcon icon={faChevronLeft} className="back-icon" />
        <span>Back to Gallery</span>
      </Button>
    );
  }, [accessTokenQueryParam, history, item, location.hash, location.state]);

  const renderHeader = useCallback(() => {
    const currentImageNumber = imageNumber;
    const galleryMediaLength = (item?.media || []).length;

    return (
      <div className={`header ${template}`}>
        {renderBackButton()}
        {galleryMediaLength !== 0 && galleryMediaLength >= currentImageNumber && (
          <span className="pagination">
            <span className="current">{currentImageNumber} </span>/ {galleryMediaLength}
          </span>
        )}

        <div className="sizing-wrap">
          <div className="button-group">
            <span>Resolution</span>
            <Button
              className={`button resolution low ${imgResolution === 'low' ? 'active' : ''}`}
              onClick={handleResolutionChange}
              disabled={!item}
            />
            <Button
              className={`button resolution medium ${imgResolution === 'medium' ? 'active' : ''}`}
              onClick={handleResolutionChange}
              disabled={!item}
            />
            <Button
              className={`button resolution high ${imgResolution === 'high' ? 'active' : ''}`}
              onClick={handleResolutionChange}
              disabled={!item}
            />
          </div>
          <div className="auto-size-wrap">
            <span>Auto-size</span>
            <Toggle
              className="toggle-button"
              checked={autoSize}
              icons={false}
              onChange={toggleAutoSize}
              disabled={!item}
            />
          </div>
        </div>
      </div>
    );
  }, [autoSize, handleResolutionChange, imageNumber, imgResolution, item, renderBackButton, template, toggleAutoSize]);

  const renderImageNavigation = useCallback(() => {
    if (!item) return;
    const { media } = item;

    const renderNavigationButton = (type) => {
      const onClick = () =>
        type === 'left' ? handleImageNavigationButtonClick('left') : handleImageNavigationButtonClick('right');
      return (
        <div
          className={`nav-button ${template} ${type}`}
          ref={type === 'left' ? leftNavigationButton : rightNavigationButton}
          onClick={onClick}
        >
          <Button className="button">
            {type === 'left' && <FontAwesomeIcon icon={faChevronLeft} className="nav-icon" />}
            {type === 'right' && <FontAwesomeIcon icon={faChevronRight} className="nav-icon" />}
          </Button>
        </div>
      );
    };

    const showLeftNavigationButton = imageNumber > 1;
    const showRightNavigationButton = media ? imageNumber < media.length : false;

    return (
      <>
        {showLeftNavigationButton && renderNavigationButton('left')}
        {showRightNavigationButton && renderNavigationButton('right')}
      </>
    );
  }, [handleImageNavigationButtonClick, imageNumber, item, template]);

  const renderRatingsContent = useCallback(() => {
    if (!item) return;
    const { media } = item;

    if (!media || media.length < imageNumber) {
      return null;
    }

    const index = imageNumber - 1;
    const mediaUUID = media[index].UUID;

    return (
      <div className="ratings-wrap">
        <span className="title">How would you rate it?</span>
        <RatingSlider className="rating-slider" submitRating={onRate} userRating={ratings ? ratings[mediaUUID] : 0} />
      </div>
    );
  }, [imageNumber, item, onRate, ratings]);

  const renderContent = useCallback(() => {
    if (!item) return;
    const { media } = item;

    if (!media || media.length < imageNumber) {
      return null;
    }

    const index = imageNumber - 1;
    const onLastImage = imageNumber === media.length;

    const previousIndex = index === 0 ? 0 : index - 1;
    const nextIndex = index + 1;

    const activeImg = (media[index] || []).src[imgResolution];
    const previousImg = (media[previousIndex] || []).src[imgResolution];
    const nextImg = !onLastImage ? (media[nextIndex] || []).src[imgResolution] : false;

    const ratingsModal = ratingsActive ? (
      <Modal onClose={toggleRatings} className="ratings-modal">
        {renderRatingsContent()}
      </Modal>
    ) : null;

    return (
      <>
        {ratingsModal}
        {slideshowProgressBarVisible && (
          <SlideshowProgress className={`${!slideshowPlaying ? 'paused' : 'active'}`} slideshowSpeed={slideshowSpeed} />
        )}
        <div className={`img-container${autoSize ? ' autosize' : ''}`}>
          <img alt="" src={activeImg} onLoad={handleImgLoad} data-image="current" data-index={imageNumber} />
          {previousImg && <img className="img-preload" alt="" src={previousImg} onLoad={handlePrevImgLoad} />}
          {nextImg && <img className="img-preload" alt="" src={nextImg} onLoad={handleNextImgLoad} />}
        </div>
      </>
    );
  }, [
    autoSize,
    handleImgLoad,
    handleNextImgLoad,
    handlePrevImgLoad,
    imageNumber,
    imgResolution,
    item,
    ratingsActive,
    renderRatingsContent,
    slideshowPlaying,
    slideshowProgressBarVisible,
    slideshowSpeed,
    toggleRatings,
  ]);

  const renderFooter = useCallback(() => {
    const imgDownloadUrl =
      item?.media && item.media.length >= imageNumber
        ? item.media[imageNumber - 1].src_downloadable[imgResolution]
        : '';

    const footer = (
      <div className={`footer ${template}${footerCollapsed ? ' collapsed' : ''}`}>
        <div
          className={`expand-collapse-wrap ${template}`}
          onClick={item ? toggleExpandCollapse : () => {}}
          title="expand / collapse with mouse click or up / down key stroke"
          data-disabled={`${!item}`}
        >
          <Button className={`button${footerCollapsed ? ' active' : ''}`} disabled={!item}>
            <FontAwesomeIcon icon={footerCollapsed ? faCaretUp : faCaretDown} className="button-icon" />
          </Button>
        </div>
        <Button
          className="button"
          disabled={!item}
          {...(item ? { url: `${generateImgUrl}/grid${accessTokenQueryParam}` } : {})}
        >
          <FontAwesomeIcon icon={faTh} className="button-icon" />
          <span>Thumbnails</span>
        </Button>

        <Button className="button" onClick={toggleSlideshow} disabled={!item}>
          {!slideshowActive && <FontAwesomeIcon icon={faPlay} className="button-icon" />}
          {slideshowActive && <FontAwesomeIcon icon={faPause} className="button-icon" />}
          <span>{slideshowActive ? 'Pause' : 'Slideshow'}</span>
        </Button>

        <Button className="button" onClick={toggleImgFavorited} disabled={!item}>
          <FontAwesomeIcon icon={faHeart} className={`button-icon${imgFavorited ? ' active' : ''}`} />
          <span>{imgFavorited ? 'Unfavorite' : 'Favorite'}</span>
        </Button>

        <Button
          className="button"
          disabled={!item}
          target="_self"
          {...(isUser ? { url: imgDownloadUrl } : { onClick: redirectToJoinIfGuest })}
        >
          <FontAwesomeIcon icon={faArrowToBottom} className="button-icon" />
          <span>Download</span>
        </Button>

        <Button className="button" onClick={toggleRatings} disabled={!item}>
          <FontAwesomeIcon icon={faStar} className={`button-icon favorite-icon${imgRated ? ' active' : ''}`} />
          <span>{imgRated ? 'Rated' : 'Rate'}</span>
        </Button>
      </div>
    );
    return (
      <div className="footer-wrap">
        {renderBackButton()}
        {footer}
      </div>
    );
  }, [
    footerCollapsed,
    accessTokenQueryParam,
    generateImgUrl,
    imageNumber,
    imgFavorited,
    imgRated,
    imgResolution,
    isUser,
    item,
    redirectToJoinIfGuest,
    renderBackButton,
    slideshowActive,
    template,
    toggleExpandCollapse,
    toggleImgFavorited,
    toggleRatings,
    toggleSlideshow,
  ]);

  const showLoader = !item || !imgLoaded;

  const imageNavigationMarkup = item ? renderImageNavigation() : null;
  const contentMarkup = item && loaded ? renderContent() : null;

  return (
    <ImageWrapper className={cx({ idle: userIsIdle })}>
      <Helmet>
        <meta name="viewport" content="width=device-width, height=device-height, user-scalable=no, initial-scale=1.0" />
        <meta property="og:url" content={`https://www.${site.domain}${item?.path}`} />
        <meta name="robots" content="noindex" />
        <title>{generateMetaTitle}</title>
        <meta property="og:title" content={generateMetaTitle} />
      </Helmet>
      {showLoader && (
        <ImageViewerPlaceholder
          // @ts-ignore
          className="imageViewer-loader"
          reloadFunction={loadData}
        />
      )}
      {renderHeader()}
      {imageNavigationMarkup}
      {contentMarkup}
      {renderFooter()}
    </ImageWrapper>
  );
}

const ImageWrapper = styled.div`
  position: fixed;
  overflow: auto;
  z-index: 9999999999;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: ${(props) => props.theme.primary1};
  font-size: 0.85rem;
  -webkit-user-select: none;
  user-select: none;

  .placeholder {
    background-color: ${(props) => props.theme.primary1};
    z-index: 1;
    position: fixed;
  }

  > div {
    transition: opacity 0.3s ease;
  }

  &.idle {
    .footer:not(.collapsed),
    .header,
    .nav-button,
    .footer-wrap .button.back {
      opacity: 0;
      pointer-events: none;
    }
  }

  @media (max-width: 767px) {
    height: 100%;
  }

  & .imageViewer-loader {
    position: absolute;
    height: 100vh;
    margin: 0;
    z-index: 1;
    top: 0;
    width: 100vw;
    min-width: 100%;
    background: ${(props) => props.theme.primary1};
  }

  & .header {
    position: fixed;
    width: 100%;
    top: 0;
    display: flex;
    height: 40px;
    padding: 0 20px;
    justify-content: space-between;
    align-items: center;
    z-index: 2;
    background: rgba(255, 255, 255, 0.5);
    font-size: 1.05rem;

    &.dark {
      background: rgba(0, 0, 0, 0.8);
    }
  }

  & .button {
    background: transparent;
    border: none;
    outline: none;
    box-shadow: none;

    span {
      @media (max-width: 767px) {
        display: none;
      }
    }
  }

  & .button:not(.resolution) {
    @media (min-width: 1036px) {
      opacity: 0.6;
    }

    transition: opacity 0.3s ease;

    :hover {
      opacity: 1;
    }
  }

  & .button-group {
    display: flex;
    align-items: center;
    padding-right: 36px;

    @media (max-width: 767px) {
      padding-right: 0;

      span {
        display: none;
      }
    }

    span {
      line-height: 12px;
      padding-right: 10px;
    }
  }

  & .button.back {
    display: flex;
    justify-content: space-between;
    width: 150px;
    align-items: center;
    height: 20px;
    text-decoration: none;
    white-space: nowrap;
  }

  & .back-icon {
    color: #4a90e2;
    font-size: 1.15rem;

    @media (max-width: 767px) {
      font-size: 1.72rem;
    }
  }

  & .button.resolution {
    background: #444444;
    margin: 0 5px;
    border-radius: 2px;
    transition: background 0.3s ease;

    @media (max-width: 1036px) {
      padding: 0;
    }
  }

  & .button.resolution:hover {
    background: #4a90e2;
  }

  & .button.resolution.active {
    background: #4a90e2;
  }

  & .resolution.low {
    height: 14px;
    width: 12px;
  }

  & .resolution.medium {
    height: 20px;
    width: 18px;
  }

  & .resolution.high {
    height: 24px;
    width: 22px;
  }

  & .pagination {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 8px;
    padding: 0;
    margin: 0;

    .current {
      // color: ${(props) => props.theme.primary3};
    }
  }

  & .sizing-wrap {
    flex-direction: row;
    display: flex;

    .auto-size-wrap {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;

      @media (max-width: 767px) {
        display: none;
      }

      span {
        padding-right: 12px;
      }
    }
  }

  & .toggle-button.react-toggle--checked {
    .react-toggle-track {
      background: #1d3e65;
    }

    .react-toggle-thumb {
      background: #4a90e2;
      border-color: #4a90e2;
      left: 16px;
    }
  }

  & .toggle-button.react-toggle--checked:hover {
    .react-toggle-track {
      background: #1d3e65;
    }
  }

  & .toggle-button .react-toggle-track {
    height: 14px;
    width: 33px;
  }

  & .toggle-button .react-toggle-thumb {
    height: 16px;
    width: 16px;
    top: -1px;
    background: #adadad;
    border-color: #adadad;
  }

  .nav-button {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 80px;
    background: rgba(255, 255, 255, 0.3);
    margin: 0 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 3px;
    z-index: 2;
    cursor: pointer;

    &.dark {
      background: rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 767px) {
      width: 32px;
      height: 48px;
      margin: 0 16px;
    }

    @media (max-width: 1036px) {
      margin: 0 20px;
    }

    .button {
      font-size: 2.6rem;
      color: #444444;

      @media (max-width: 767px) {
        font-size: 1.4rem;
      }
    }
  }

  & .nav-button.left {
    left: 0;
  }

  & .nav-button.right {
    right: 0;
  }

  @keyframes progressUp {
    0% {
      width: 0;
    }
    100% {
      width: 100%;
    }
  }

  & .img-container {
    align-items: center;
    background: #000;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    left: 0;
    margin: auto;
    overflow: hidden;
    position: absolute;
    right: 0;
    top: 0;

    img {
      cursor: move;
      display: block;

      &.img-preload {
        display: none;
        width: 1px;
        height: 1px;
      }
    }
  }

  & .img-container.autosize {
    height: 100%;
    max-height: 100vh;
    max-width: 100vw;
    width: 100%;

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }

  & .ratings-modal {
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    z-index: 2;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 767px) {
      height: 100%;
    }

    .close-button {
      position: absolute;
      top: 0;
      background: transparent;
      border: none;
      font-size: 2.15rem;
      color: white;
      right: 16px;
      outline: none;

      @media (max-width: 767px) {
        right: 15px;
        margin: 0;
        padding: 0;
      }
    }
  }

  & .ratings-wrap {
    width: 280px;
    height: 152px;
    background: rgba(0, 0, 0, 1);
    display: flex;
    flex-direction: column;
    -webkit-box-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    align-items: center;

    .rating-slider {
      top: 8px;
      margin: 24px 0;

      .mark-text {
        color: #ffffff;
      }

      .star {
        top: -5px;
        font-size: 1.4rem;
      }

      .rc-slider-dot {
        background: #000;
      }
    }

    span.title {
      color: #ffffff;
      padding: 10px;
      font-size: 1rem;
    }
  }

  & .footer-wrap {
    position: fixed;
    padding: 0 20px;
    width: 100%;
    bottom: 15px;
    height: 54px;
    display: flex;
    align-items: center;
    z-index: 2;

    .button.back {
      span {
        font-size: 1.05rem;
      }

      @media (max-width: 1036px) {
        display: none;
      }
    }
  }

  & .footer {
    position: fixed;
    z-index: 1;
    bottom: 15px;
    display: flex;
    flex-direction: row;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.8);
    justify-content: center;
    align-items: center;
    border-radius: 3px;
    transition: 0.3s transform ease;

    &.dark {
      background: rgba(0, 0, 0, 0.8);
    }

    &.collapsed {
      transform: translate3d(-50%, 70px, 0);
    }

    @media (max-width: 1036px) {
      bottom: 20px;
      justify-content: center;
      align-items: center;

      @media (max-width: 767px) {
        width: 90%;
        bottom: 10px;
      }
    }

    .expand-collapse-wrap {
      position: absolute;
      top: -30px;
      width: 80px;
      border-radius: 8px 8px 0 0;
      background: rgba(255, 255, 255, 0.8);
      height: 30px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      &.dark {
        background: rgba(0, 0, 0, 0.8);
      }

      &:not([data-disabled='true']):hover {
        > .button {
          opacity: 1;
        }
      }

      @media (max-width: 1036px) {
        display: none;
      }

      > .button {
        margin: 0;
        padding: 0;
        position: absolute;
        width: 20px;
        height: 20px;

        &.active {
          color: #4a90e2;
        }

        svg {
          position: absolute;
          margin: 0;
          font-size: 2.4rem;
        }
      }
    }

    .button,
    a {
      position: relative;
      margin: 16px 20px;
      text-decoration: none;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1.05rem;

      @media (max-width: 767px) {
        margin: 16px 15px;
        padding: 0;
      }

      .button-icon {
        margin: 0 8px;
        font-size: 1.4rem;

        @media (max-width: 767px) {
          font-size: 1.72rem;
        }
      }

      .button-icon.active {
        color: #e61638;
      }

      .button-icon.favorite-icon.active {
        color: #ffc307;
      }

      span {
        white-space: nowrap;
        font-size: 1.05rem;
        -webkit-font-smoothing: antialiased;
        @media (max-width: 1036px) {
          display: none;
        }
      }
    }
  }
`;
